import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Car, Calendar, Users, MapPin, CheckCircle, Info, DollarSign } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const PrimateExpress = () => {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    startDate: '',
    notes: ''
  });

  const vehicles = [
    { id: 'land-cruiser', name: 'Toyota Land Cruiser', price: 150 },
    { id: 'land-cruiser-70', name: 'Land Cruiser 70 Series', price: 155 },
    { id: 'hiace', name: 'HiAce Safari Van', price: 120 }
  ];

  const addons = [
    { id: 'child-seat', name: 'Child Car Seat', price: 5 },
    { id: 'mifi', name: 'Portable WiFi', price: 10 },
    { id: 'roof-tent', name: 'Rooftop Car Tent', price: 30 },
    { id: 'camping-gear', name: 'Camping Equipment', price: 40 }
  ];

  const calculateTotal = () => {
    const vehiclePrice = vehicles.find(v => v.id === selectedVehicle)?.price || 0;
    const addonsTotal = selectedAddons.reduce((total, addonId) => {
      const addon = addons.find(a => a.id === addonId);
      return total + (addon?.price || 0);
    }, 0);
    return vehiclePrice + addonsTotal;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formBody = {
        'form-name': 'safari-quote-request',
        ...formData,
        package: 'The Primate Express',
        vehicle: vehicles.find(v => v.id === selectedVehicle)?.name,
        addons: selectedAddons.map(id => addons.find(a => a.id === id)?.name).join(', '),
        totalPrice: calculateTotal()
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your request. Please try again.');
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/PK2H77z.jpg"
            alt="The Primate Express Safari"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/self-drive-safaris" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Self-Drive Safaris
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">8-Day The Primate Express</h1>
            <p className="text-lg text-white/90 mb-6">
              An unforgettable journey focused on Uganda's incredible primates, including mountain gorillas and chimpanzees.
            </p>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Itinerary Overview</h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Days 1-2: Kibale Forest</h3>
                  <p className="text-gray-600">
                    Begin your primate adventure in Kibale Forest, home to 13 primate species. 
                    Experience chimpanzee tracking and a night walk to spot nocturnal primates.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Days 3-4: Queen Elizabeth National Park</h3>
                  <p className="text-gray-600">
                    Enjoy game drives and a boat cruise while en route to Bwindi. Look for tree-climbing 
                    lions and diverse wildlife.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Days 5-6: Bwindi Impenetrable Forest</h3>
                  <p className="text-gray-600">
                    The highlight of your journey - gorilla trekking in Bwindi. Spend time with these 
                    magnificent creatures in their natural habitat.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Days 7-8: Mgahinga National Park</h3>
                  <p className="text-gray-600">
                    Track golden monkeys and enjoy hiking in the Virunga Mountains before returning to Kampala.
                  </p>
                </div>
              </div>

              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">What's Included</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                      <span>Detailed route maps and itinerary</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                      <span>Park entrance fees</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                      <span>Accommodation bookings</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                      <span>24/7 support hotline</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">What's Not Included</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <li className="flex items-start">
                      <Info className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      <span>Gorilla and chimp permits</span>
                    </li>
                    <li className="flex items-start">
                      <Info className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      <span>Fuel costs</span>
                    </li>
                    <li className="flex items-start">
                      <Info className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      <span>Meals and drinks</span>
                    </li>
                    <li className="flex items-start">
                      <Info className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      <span>Travel insurance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Request Quote</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                      <select
                        value={selectedVehicle}
                        onChange={(e) => setSelectedVehicle(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      >
                        <option value="">Select a vehicle</option>
                        {vehicles.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name} (${vehicle.price}/day)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Add-ons</label>
                      <div className="space-y-2">
                        {addons.map(addon => (
                          <label key={addon.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedAddons.includes(addon.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAddons([...selectedAddons, addon.id]);
                                } else {
                                  setSelectedAddons(selectedAddons.filter(id => id !== addon.id));
                                }
                              }}
                              className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {addon.name} (+${addon.price}/day)
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        required
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      ></textarea>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center text-sm font-medium">
                        <span>Daily Rate:</span>
                        <span>${calculateTotal()}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm font-medium mt-2">
                        <span>8-Day Package:</span>
                        <span>${calculateTotal() * 8}</span>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
                    >
                      Request Quote
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PrimateExpress;